import { useCallback, useEffect, useMemo, useState } from 'react';
import type { User } from 'oidc-client-ts';
import { getConfig } from '@/config/runtime-config';
import { clearUser, setUser } from '@/config/faro';
import { getOidcManager, getStoredOidcUser } from '@/services/oidcService';
import { oAuthService } from '@/services/oAuthService';
import type { AuthAccount, AuthHookResult } from '@/types/auth';

export type UseOidcAuthReturn = AuthHookResult;

function mapUserToAccount(user: User | null): AuthAccount | null {
  if (!user) {
    return null;
  }

  const profile = user.profile;
  const subject = typeof profile.sub === 'string' ? profile.sub : undefined;
  const preferredUsername =
    typeof profile.preferred_username === 'string'
      ? profile.preferred_username
      : typeof profile.email === 'string'
        ? profile.email
        : undefined;
  const name = typeof profile.name === 'string' ? profile.name : preferredUsername;
  const tenantId = typeof profile.tid === 'string' ? profile.tid : undefined;

  return {
    homeAccountId: subject,
    localAccountId: subject,
    tenantId,
    username: preferredUsername,
    name,
    sub: subject,
  };
}

export const useOidcAuth = (): UseOidcAuthReturn => {
  const config = getConfig();
  const manager = useMemo(() => getOidcManager(config), [config]);

  const [account, setAccount] = useState<AuthAccount | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const clearToken = useCallback(() => {
    setAccessToken(null);
    setAccount(null);
    oAuthService.clearToken();
    clearUser();
  }, []);

  const updateUserState = useCallback((user: User | null) => {
    if (!user || user.expired || !user.access_token) {
      clearToken();
      return;
    }

    const mappedAccount = mapUserToAccount(user);
    setAccount(mappedAccount);
    setAccessToken(user.access_token);

    if (typeof user.expires_at === 'number') {
      oAuthService.setAccessToken(user.access_token, new Date(user.expires_at * 1000));
    } else {
      oAuthService.setAccessToken(user.access_token);
    }

    if (mappedAccount?.localAccountId) {
      setUser(mappedAccount.localAccountId, {
        username: mappedAccount.username || 'unknown',
        tenantId: mappedAccount.tenantId || 'unknown',
      });
    }
  }, [clearToken]);

  const acquireTokenSilently = useCallback(async (): Promise<string | null> => {
    setError(null);

    try {
      const user = await manager.signinSilent();
      updateUserState(user);
      return user.access_token;
    } catch (authError) {
      const message = authError instanceof Error ? authError.message : 'Failed to acquire token';
      console.error('[OIDC Auth] Silent token acquisition failed:', authError);
      setError(message);
      clearToken();
      return null;
    }
  }, [clearToken, manager, updateUserState]);

  const login = useCallback(async (): Promise<void> => {
    setError(null);
    setIsLoading(true);

    try {
      const returnPath = `${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`;
      await manager.signinRedirect({ state: { returnPath } });
    } catch (authError) {
      const message = authError instanceof Error ? authError.message : 'Login failed';
      console.error('[OIDC Auth] Login failed:', authError);
      setError(message);
      setIsLoading(false);
      throw authError;
    }
  }, [manager]);

  const logout = useCallback(async (): Promise<void> => {
    setError(null);
    setIsLoading(true);

    try {
      clearToken();
      await manager.removeUser();
      await manager.signoutRedirect();
    } catch (authError) {
      const message = authError instanceof Error ? authError.message : 'Logout failed';
      console.error('[OIDC Auth] Logout failed:', authError);
      setError(message);
      setIsLoading(false);
      throw authError;
    }
  }, [clearToken, manager]);

  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        const user = await getStoredOidcUser(config);
        if (isMounted) {
          updateUserState(user);
        }
      } catch (authError) {
        if (isMounted) {
          const message = authError instanceof Error ? authError.message : 'Failed to initialize authentication';
          console.error('[OIDC Auth] Initialization failed:', authError);
          setError(message);
          clearToken();
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void initializeAuth();

    const handleUserLoaded = (user: User) => {
      updateUserState(user);
      setError(null);
      setIsLoading(false);
    };

    const handleUserUnloaded = () => {
      clearToken();
      setIsLoading(false);
    };

    const handleSilentRenewError = (renewError: Error) => {
      console.error('[OIDC Auth] Silent renew failed:', renewError);
      setError(renewError.message);
    };

    manager.events.addUserLoaded(handleUserLoaded);
    manager.events.addUserUnloaded(handleUserUnloaded);
    manager.events.addUserSignedOut(handleUserUnloaded);
    manager.events.addSilentRenewError(handleSilentRenewError);

    oAuthService.setTokenRefreshCallback(async () => acquireTokenSilently());

    return () => {
      isMounted = false;
      manager.events.removeUserLoaded(handleUserLoaded);
      manager.events.removeUserUnloaded(handleUserUnloaded);
      manager.events.removeUserSignedOut(handleUserUnloaded);
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, [acquireTokenSilently, clearToken, config, manager, updateUserState]);

  return {
    account,
    isAuthenticated: Boolean(accessToken),
    accessToken,
    isLoading,
    error,
    login,
    logout,
    acquireTokenSilently,
  };
};